import { get_reorder_steps_url } from '@/utils/urls.ts';
import { useState } from 'react';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';

type reorderStepsParams = {
  step_id: string;
  new_parent_step_id?: string;
  condition_id?: string;
  new_step_index: number;
};

type ReorderStepsOptions = {
  onError?: () => void;
};

const useReorderSteps = () => {
  const apiClient = useApiClient();
  const { addToQueue, reportLatestDraftChanged } = useEditVersionContext();
  const [pendingCount, setPendingCount] = useState(0);
  const { target } = useActiveCompositionTarget();
  const { mutate: applyComposition, isPending: isCompositionPending } = useCompositionOperations();

  const reorderSteps = (params: reorderStepsParams, options?: ReorderStepsOptions) => {
    if (target.scope_kind === 'local_subflow' && target.subflow_id) {
      applyComposition(
        [{
          op_type: 'move_local_subflow_step',
          subflow_id: target.subflow_id,
          step_id: params.step_id,
          target_parent_step_id: params.new_parent_step_id ?? null,
          target_child_group_key: params.condition_id ?? null,
          target_index: params.new_step_index,
        }],
        { onSuccess: () => undefined, onError: options?.onError },
      );
      return;
    }
    setPendingCount((count) => count + 1);
    addToQueue({
      mutationFn: async (latestVersion) => {
        const response = await apiClient.put(get_reorder_steps_url(latestVersion.id), params, {
          suppressGlobalErrorToast: true,
        });
        return response.data;
      },
      onSuccess: () => {},
      onError: (error) => {
        notifyStepMutationError('reorder steps', error, reportLatestDraftChanged);
        options?.onError?.();
      },
      onComplete: () => {
        setPendingCount((count) => count - 1);
      },
    });
  };

  return { mutate: reorderSteps, isPending: pendingCount > 0 || isCompositionPending };
};

export default useReorderSteps;
